/**
 * Client-side payment-flow monitoring for web-portal-2.
 *
 * Reports anomalies that happen in the browser during checkout — things that
 * never reach our API (client-state guards, navigation/validation failures) or
 * unexpected responses the client can't reconcile (e.g. a successful `/confirm`
 * whose payload is missing the persisted id). Provider SDK failures are handled
 * separately inside the card-form components via `reportClientPaymentError`
 * (`@butter/monitoring`) — see the disambiguation note below.
 *
 * Coverage is intentionally BROAD during early onboarding. Every event is tagged
 * (`domain`, `surface`, `payment_error_kind`, `operation`) so it can be filtered
 * or dialed down from this single place later, rather than sprinkled edits.
 *
 * ⚠ NOT to be confused with `reportClientPaymentError` (from `@butter/monitoring`) —
 * the shared reporter for provider confirm/tokenize failures on the add-payment
 * surfaces. This local `reportPaymentClientError` reports checkout-FLOW anomalies
 * (state/navigation/response). Similar names, different jobs; a rename to
 * disambiguate is a deferred follow-up.
 *
 * @module features/payment/monitoring
 */

import { captureException } from '@butter/monitoring';

/** How the failure should be read when triaging in Sentry. */
export type PaymentClientErrorKind =
  | 'client_flow' // corrupted/incomplete client state (missing ids, provider not detected)
  | 'response_anomaly' // API succeeded but the client can't use the response
  | 'navigation' // step/auto-advance/validation failure that never hits the API
  | 'mutation'; // a charge/save mutation rejected (also server-logged; captured for parity)

export interface ReportPaymentClientErrorOptions {
  /** Sentry level. Anomalies default to 'error'; routine validation to 'warning'. */
  level?: 'error' | 'warning' | 'info';
  /** Triage class — see {@link PaymentClientErrorKind}. */
  kind?: PaymentClientErrorKind;
  /** Which checkout the failure came from. */
  surface?: string;
  /** Structured context for the Sentry event. */
  extra?: Record<string, unknown>;
}

/**
 * Report a client-side payment-flow anomaly to Sentry with consistent tags.
 *
 * @param operation short, stable slug for the failing step (e.g. `confirm_missing_db_id`)
 * @param error the thrown value or an Error describing the anomaly
 */
export function reportPaymentClientError(
  operation: string,
  error: unknown,
  options: ReportPaymentClientErrorOptions = {}
): void {
  captureException(error instanceof Error ? error : new Error(String(error)), {
    level: options.level ?? 'error',
    tags: {
      domain: 'payment',
      client_side: true,
      surface: options.surface ?? 'web_checkout',
      payment_error_kind: options.kind ?? 'client_flow',
      operation,
    },
    extra: options.extra,
  });
}
